// castLayout.ts — Near-wall casting yard layout algorithm.
// Panels are placed flat (exterior face down) near their final erected wall positions.
// Crane sits at the NW (back-left) corner of the usable slab area.
//
// COORDINATE CONVENTION: Y/Z increases going SOUTH (rows A-Z head south) and X
// increases going EAST (columns 1-50 head east).  minZ ≈ 0 = NORTH, maxZ > 0 = SOUTH.
// This matches SVG/screen convention (Y-down = south) — opposite geographic math.

import type { PanelData } from '../types/panelvar';
import type { CastLayoutSettings } from '../types/panelvar';
import { calcMaterials } from './materialsCalc';
import { craneRings } from './craneCapacity';

/** One wall's data for cast layout — mirrors WallSegment geometry */
export interface WallCastRow {
  panels:      PanelData[];
  startX:      number;   // wall anchor X in plan coords (inches)
  startZ:      number;   // wall anchor Z in plan coords (inches)
  rotationRad: number;   // wall angle in radians (same as WallSegment.rotationY)
  leftJoint:   number;   // left joint width (inches)
  rightJoint:  number;   // right joint width (inches)
}

export interface CastPlacedPanel {
  panel:        PanelData;
  cx:           number;   // center X in plan coords (inches)
  cy:           number;   // center Z in plan coords (inches)
  w:            number;   // panel width (along wall / cast width)
  h:            number;   // panel height (as-erected / cast length)
  rotationRad:  number;   // wall angle for SVG transform rotation
  weightTons:   number;
  pickOrder:    number;
  pickRadiusIn: number;
  isStacked:    boolean;
  stackLevel:   number;
  stackedOnId?: string;
  isNearWall:   boolean;  // true = placed adjacent to wall; false = central snake
}

export interface CastLayoutResult {
  placed:               CastPlacedPanel[];
  craneX:               number;
  craneY:               number;   // Z in world plan coords
  craneExclusionActual: number;   // = CRANE_BODY_RADIUS_IN + settings.craneExclusionIn
  usableMinX:           number;
  usableMinY:           number;
  usableMaxX:           number;
  usableMaxY:           number;
  /** Panel IDs in clockwise perimeter-walk order — used to seed the sidebar pick order. */
  perimOrder:           string[];
}

// ── Interior direction in plan (x,z): (sinA, cosA) ───────────────────────────
// Along-wall direction in plan (x,z): (cosA, -sinA)

export function castLayout(
  walls: WallCastRow[],
  slabCorners: [number, number][],
  settings: CastLayoutSettings,
  pickOrderOverride?: string[],
): CastLayoutResult {
  // ── 1. Usable bounding box ────────────────────────────────────────────────
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const [x, z] of slabCorners) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  if (!Number.isFinite(minX)) { minX = 0; maxX = 1440; minZ = 0; maxZ = 720; }

  const c = settings.clearanceIn;
  const usableMinX = minX + c;
  const usableMaxX = maxX - c;
  const usableMinY = minZ + c;
  const usableMaxY = maxZ - c;

  // ── COORDINATE SYSTEM NOTE ──────────────────────────────────────────────────
  // Building grid: rows A–Z head SOUTH (Y/Z increases southward) and columns
  // 1–50 head EAST (X increases eastward).  This matches SVG convention
  // (positive Y = down = south) but is OPPOSITE to standard geographic math
  // (where north = +Y).
  //
  //   minX ≈ 0  = WEST wall   (left,  small X)
  //   maxX > 0  = EAST wall   (right, large X)
  //   minZ ≈ 0  = NORTH wall  (back of building,  small Y)  ← "top" in plan view
  //   maxZ > 0  = SOUTH wall  (front of building, large Y)  ← "bottom" in plan view
  //
  //   usableMinY = minZ + clearanceIn ≈ clearanceIn  → inner NORTH/back boundary
  //   usableMaxY = maxZ - clearanceIn               → inner SOUTH/front boundary
  //
  // Three.js mapping: planX → world.X,  planY → world.Z  (positive, no flip)
  // ────────────────────────────────────────────────────────────────────────────

  // LR1300 half-footprint: tracks are 40ft × 40ft in plan
  // (TRACK_SPREAD 29.5ft + TRACK_W 10.5ft = 40ft full width; TRACK_D 40ft long).
  // Half-extent in both X and Z = 20ft = 240".
  const CRANE_BODY_RADIUS_IN = 240; // 20 ft — LR1300 track half-footprint (used for exclusion zone)

  // Crane center: start with inner ring tangent to the clearance lines.
  // Then iteratively check every slab polygon segment: if the ring crosses a wall,
  // push the crane inward (along that wall's interior normal) until tangent.
  // This handles L-shaped / stepped slabs where the AABB corner is in a void —
  // the crane shifts to the "next wall clearance line" automatically.
  const minPickRadius = craneRings(settings.craneModel)[0].radiusIn;
  let craneX = usableMinX + minPickRadius;
  let craneY = usableMinY + minPickRadius;

  if (slabCorners.length >= 3) {
    const n   = slabCorners.length;
    const c   = settings.clearanceIn;
    const midX = (minX + maxX) / 2;
    const midZ = (minZ + maxZ) / 2;

    // Two passes: south-pushing walls first (adjusts craneY), then east-pushing
    // (adjusts craneX with already-updated craneY).  Processing Y before X lets
    // the Y shift resolve the inner vertical step wall too — because after craneY
    // moves south, the ring no longer reaches that vertical segment.
    //
    // For each relevant segment we use the SEGMENT distance (not infinite-line)
    // so we don't falsely trigger on walls the ring only approaches asymptotically.
    // When a crossing IS found, we push to the CLEARANCE-LINE tangent
    // (line-dist = R + clearanceIn), not the wall itself.
    for (const pass of [0, 1] as const) {
      for (let i = 0; i < n; i++) {
        const [x1, z1] = slabCorners[i];
        const [x2, z2] = slabCorners[(i + 1) % n];
        const dx = x2 - x1, dz = z2 - z1;
        const segLen = Math.hypot(dx, dz);
        if (segLen < 1) continue;

        // Inward normal — orient toward building centre (winding-order independent)
        const rpx = -dz / segLen, rpz = dx / segLen;
        const smx = (x1 + x2) / 2, smz = (z1 + z2) / 2;
        const d   = rpx * (midX - smx) + rpz * (midZ - smz);
        const nx  = d >= 0 ? rpx : -rpx;
        const nz  = d >= 0 ? rpz : -rpz;

        // Pass 0: south-pushing walls (nz > 0.5)
        // Pass 1: east-pushing walls  (nx > 0.5)
        if (pass === 0 && nz <= 0.5) continue;
        if (pass === 1 && nx <= 0.5) continue;

        // Nearest point on the segment to the current crane position
        const t     = Math.max(0, Math.min(1,
          ((craneX - x1) * dx + (craneY - z1) * dz) / (segLen * segLen)));
        const nearX = x1 + t * dx;
        const nearZ = z1 + t * dz;
        const segDist = Math.hypot(craneX - nearX, craneY - nearZ);

        if (segDist < minPickRadius) {
          // Ring crosses this wall segment → push to clearance-line tangent
          const lineDist = nx * (craneX - x1) + nz * (craneY - z1);
          const push     = minPickRadius + c - lineDist;
          craneX += Math.max(0, nx * push);
          craneY += Math.max(0, nz * push);
        }
      }
    }
  }

  const placed: CastPlacedPanel[] = [];

  // ── Shared: cast-joint gap + slab polygon helpers ─────────────────────────
  // 1.5" = one form board (standard cast-joint gap between all adjacent panels,
  // whether near-wall or interior overflow).
  const CAST_JOINT_GAP = 1.5;

  // Ray-cast point-in-polygon test against slab corners.
  function pointInSlab(px: number, py: number): boolean {
    let inside = false;
    const n = slabCorners.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = slabCorners[i][0], yi = slabCorners[i][1];
      const xj = slabCorners[j][0], yj = slabCorners[j][1];
      if ((yi > py) !== (yj > py) &&
          px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  // Check that the axis-aligned bounding box of a panel (given by half-extents
  // hw/hh) is entirely within both the usable AABB and the slab polygon.
  // For rotated near-wall panels pass halfExtX/halfExtZ as hw/hh.
  function panelInsideSlab(cx: number, cy: number, hw: number, hh: number): boolean {
    const corners: [number, number][] = [
      [cx - hw, cy - hh], [cx + hw, cy - hh],
      [cx + hw, cy + hh], [cx - hw, cy + hh],
    ];
    return corners.every(([px, py]) =>
      px >= usableMinX && px <= usableMaxX &&
      py >= usableMinY && py <= usableMaxY &&
      pointInSlab(px, py)
    );
  }

  // ── 2. Near-wall placement (multi-row per wall) ────────────────────────────
  // Each panel is cast adjacent to the wall where it will be erected — as close
  // as possible to its installed position and aligned parallel to the wall for
  // easy forming. Row 1 sits at clearanceIn depth from the wall face; when a
  // panel can't fit in row 1 (bounds fail after the exclusion-zone shift) it
  // retries in row 2 directly behind row 1, and so on. No central snake —
  // every panel stays near its own wall.
  //
  // Pick order follows the perimeter clockwise starting from the crane's corner
  // (NW = minX, minZ). The walls array from buildWallCastRows is already in
  // polygon order from computeFootprint — we just rotate the sequence so the
  // wall whose start is nearest (minX, minZ) comes first. Panel order within
  // each wall is preserved (natural left→right in chain direction).
  let perimStartIdx = 0;
  {
    let perimMinDist = Infinity;
    for (let i = 0; i < walls.length; i++) {
      const d = Math.hypot(walls[i].startX - minX, walls[i].startZ - minZ);
      if (d < perimMinDist) { perimMinDist = d; perimStartIdx = i; }
    }
  }
  const sortedWalls = [
    ...walls.slice(perimStartIdx),
    ...walls.slice(0, perimStartIdx),
  ];

  // Perimeter walk order — always computed from the polygon chain regardless of
  // any user override.  Exposed in the result so the sidebar can seed itself
  // with the correct initial order without re-deriving it from castResult.placed.
  const perimOrder: string[] = [];
  for (const wall of sortedWalls) {
    for (const panel of wall.panels) perimOrder.push(panel.id);
  }

  // Pre-assign pick orders — either from a user-supplied override or the
  // clockwise perimeter walk.  Pre-assigning before placement ensures clash
  // evictions can't corrupt the sequence.
  const perimPickOrder = new Map<string, number>();
  if (pickOrderOverride && pickOrderOverride.length > 0) {
    pickOrderOverride.forEach((id, i) => perimPickOrder.set(id, i + 1));
    let next = pickOrderOverride.length + 1;
    for (const id of perimOrder) {
      if (!perimPickOrder.has(id)) perimPickOrder.set(id, next++);
    }
  } else {
    perimOrder.forEach((id, i) => perimPickOrder.set(id, i + 1));
  }

  for (const wall of sortedWalls) {
    const A       = wall.rotationRad;
    const cosA    = Math.cos(A);
    const sinA    = Math.sin(A);
    const absCosA = Math.abs(cosA);
    const absSinA = Math.abs(sinA);

    let pending  = wall.panels.slice();
    let rowFront = settings.clearanceIn; // depth of row near-edge from wall face

    for (let row = 0; row < 8 && pending.length > 0; row++) {
      const nextRow: PanelData[] = [];
      let rowMaxH = 0;
      let offset  = wall.leftJoint;

      for (const panel of pending) {
        const w = panel.mpvar.width;
        const h = panel.mpvar.height;

        // Near-wall panels always cast in wall orientation: w along wall, h inward.
        // All panels (portrait and landscape) are placed near their own wall so that
        // pick order matches install order and cast rows sit on the clearance line.
        offset += w / 2;

        const wallFaceX = wall.startX + offset * cosA;
        const wallFaceZ = wall.startZ - offset * sinA;
        let   cx        = wallFaceX + (rowFront + h / 2) * sinA;
        let   cz        = wallFaceZ + (rowFront + h / 2) * cosA;

        // Crane exclusion — shift along wall until panel edge clears the ring
        const halfDiag      = Math.hypot(w / 2, h / 2);
        const effectiveExcl = Math.max(CRANE_BODY_RADIUS_IN + settings.craneExclusionIn, minPickRadius);
        const centerExcl    = effectiveExcl + halfDiag;
        if (Math.hypot(cx - craneX, cz - craneY) < centerExcl) {
          const dx0 = cx - craneX, dz0 = cz - craneY;
          const b   = cosA * dx0 - sinA * dz0;
          const cc  = dx0 * dx0 + dz0 * dz0 - centerExcl * centerExcl;
          const disc = b * b - cc;
          if (disc >= 0) {
            const sqD = Math.sqrt(disc);
            const t1 = -b + sqD, t2 = -b - sqD;
            const t  = t1 >= 0 ? t1 : t2;
            cx += t * cosA;
            cz -= t * sinA;
          }
        }

        const halfExtX = absCosA * w / 2 + absSinA * h / 2;
        const halfExtZ = absSinA * w / 2 + absCosA * h / 2;

        if (!panelInsideSlab(cx, cz, halfExtX, halfExtZ)) {
          nextRow.push(panel); // outside AABB or slab polygon — retry behind
        } else {
          const mat = calcMaterials(panel);
          placed.push({
            panel,
            cx, cy: cz,
            w, h,
            rotationRad:  -A,            // top-out: top edge toward wall (exterior face)
            weightTons:   mat.panelWeightTons,
            pickOrder:    perimPickOrder.get(panel.id) ?? placed.length + 1,
            pickRadiusIn: Math.hypot(cx - craneX, cz - craneY),
            isStacked:    false,
            stackLevel:   0,
            isNearWall:   true,
          });
          rowMaxH = Math.max(rowMaxH, h);
        }

        offset += w / 2 + CAST_JOINT_GAP;
      }

      if (rowMaxH === 0) break; // nothing fit — further rows won't help
      rowFront += rowMaxH + CAST_JOINT_GAP;
      pending = nextRow;
    }
    // Panels still pending after 8 rows: collected for interior overflow below.
  }

  // ── 2.5. Clash check ─────────────────────────────────────────────────────────
  // Adjacent-wall panels can overlap at building corners after near-wall
  // placement.  Detect pairwise AABB overlaps; the lower-priority panel (higher
  // placed-array index) is pulled out to the interior overflow queue.
  {
    const keepSet = new Set<number>(placed.map((_, i) => i));

    for (let i = 0; i < placed.length; i++) {
      if (!keepSet.has(i)) continue;
      const a   = placed[i];
      const acA = Math.abs(Math.cos(a.rotationRad));
      const asA = Math.abs(Math.sin(a.rotationRad));
      const aHX = acA * a.w / 2 + asA * a.h / 2;
      const aHY = asA * a.w / 2 + acA * a.h / 2;

      for (let j = i + 1; j < placed.length; j++) {
        if (!keepSet.has(j)) continue;
        const b   = placed[j];
        const bcA = Math.abs(Math.cos(b.rotationRad));
        const bsA = Math.abs(Math.sin(b.rotationRad));
        const bHX = bcA * b.w / 2 + bsA * b.h / 2;
        const bHY = bsA * b.w / 2 + bcA * b.h / 2;

        const gapX = Math.abs(a.cx - b.cx) - (aHX + bHX);
        const gapY = Math.abs(a.cy - b.cy) - (aHY + bHY);
        if (gapX < 0 && gapY < 0) keepSet.delete(j);
      }
    }

    if (keepSet.size < placed.length) {
      const cleaned = placed.filter((_, i) => keepSet.has(i));
      placed.length = 0;
      for (const p of cleaned) placed.push(p);
    }
  }

  // ── 3. Interior overflow snake ────────────────────────────────────────────────
  // Any panel not placed near its wall (near-wall rows exhausted or clash-evicted)
  // goes into a snake grid that fills the open interior of the slab.
  //
  // Rules:
  //  • Sorted by height DESC then pickOrder ASC so same-height panels land in
  //    the same row → consistent top/bottom pour alignment.
  //  • 1.5" cast-joint gap between panels (standard form board) instead of
  //    the perimeter 18" walking gap.
  //  • All four panel corners must be inside the slab polygon (point-in-polygon
  //    ray-cast) — prevents placement in L-shape notch areas.
  //  • Crane exclusion zone is still enforced via colX jump.
  {
    const placedIdSet = new Set(placed.map(p => p.panel.id));
    const overflow: Array<{ panel: PanelData; pickOrder: number }> = [];
    for (const id of perimOrder) {
      if (placedIdSet.has(id)) continue;
      for (const wall of sortedWalls) {
        const panel = wall.panels.find(p => p.id === id);
        if (panel) { overflow.push({ panel, pickOrder: perimPickOrder.get(id) ?? 9999 }); break; }
      }
    }

    // Overflow panels are oriented so their LONGER dimension goes along the row
    // (East-West) and the shorter dimension is the row depth (North-South).
    //
    //  • Portrait / square (h >= w): keep rotationRad = 0
    //      pw = w  (E-W)   ph = h  (N-S row depth)
    //  • Landscape (w > h): rotate 90° so h goes E-W and w is the row depth
    //      pw = h  (E-W)   ph = w  (N-S row depth)
    //
    // Sort by Math.max(w,h) DESC so same-depth panels land in the same row
    // → consistent top/bottom pour-line alignment for formwork reuse.
    overflow.sort((a, b) => {
      const da = Math.max(a.panel.mpvar.width, a.panel.mpvar.height);
      const db = Math.max(b.panel.mpvar.width, b.panel.mpvar.height);
      return db - da || a.pickOrder - b.pickOrder;
    });

    if (overflow.length > 0) {
      interface Box { minX: number; maxX: number; minY: number; maxY: number; }
      const boxes: Box[] = placed.map(p => {
        const acA = Math.abs(Math.cos(p.rotationRad)), asA = Math.abs(Math.sin(p.rotationRad));
        return {
          minX: p.cx - acA * p.w / 2 - asA * p.h / 2,
          maxX: p.cx + acA * p.w / 2 + asA * p.h / 2,
          minY: p.cy - asA * p.w / 2 - acA * p.h / 2,
          maxY: p.cy + asA * p.w / 2 + acA * p.h / 2,
        };
      });

      function clashBox(cx: number, cy: number, pw: number, ph: number): Box | null {
        const pMnX = cx - pw / 2, pMxX = cx + pw / 2;
        const pMnY = cy - ph / 2, pMxY = cy + ph / 2;
        return boxes.find(b => pMnX < b.maxX && pMxX > b.minX && pMnY < b.maxY && pMxY > b.minY) ?? null;
      }

      const effectiveExcl = Math.max(CRANE_BODY_RADIUS_IN + settings.craneExclusionIn, minPickRadius);
      // Row depth = Math.max(w,h) for each panel (longer dim is always N-S or E-W)
      const defaultH = Math.max(...overflow.map(o => Math.max(o.panel.mpvar.width, o.panel.mpvar.height)));
      let rowY   = usableMinY;
      let rowDir = 1 as 1 | -1;
      let colX   = usableMinX;
      let rowH   = 0;

      function nextRow() {
        rowY  += Math.max(rowH, defaultH) + CAST_JOINT_GAP;
        rowH   = 0;
        rowDir = (-rowDir) as 1 | -1;
        colX   = rowDir > 0 ? usableMinX : usableMaxX;
      }

      for (const { panel, pickOrder } of overflow) {
        // Landscape panels (w > h) rotate 90° → pw = h (E-W), ph = w (N-S depth)
        // Portrait/square panels keep default  → pw = w (E-W), ph = h (N-S depth)
        const landscape = panel.mpvar.width > panel.mpvar.height;
        const pw = landscape ? panel.mpvar.height : panel.mpvar.width;
        const ph = landscape ? panel.mpvar.width  : panel.mpvar.height;
        const overflowRot = landscape ? Math.PI / 2 : 0;
        const halfDiag = Math.hypot(pw / 2, ph / 2);
        const exclR    = effectiveExcl + halfDiag;
        let done = false;

        for (let attempt = 0; attempt < 600 && !done; attempt++) {
          const cx = rowDir > 0 ? colX + pw / 2 : colX - pw / 2;
          const cy = rowY + ph / 2;

          // Out of slab vertically — give up on this panel
          if (cy + ph / 2 > usableMaxY) break;

          // Row full horizontally — advance to next row
          if (rowDir > 0 && cx + pw / 2 > usableMaxX) { nextRow(); continue; }
          if (rowDir < 0 && cx - pw / 2 < usableMinX) { nextRow(); continue; }

          // Crane exclusion zone — jump colX to clear the circle on this row
          if (Math.hypot(cx - craneX, cy - craneY) < exclR) {
            const dy       = cy - craneY;
            const exclSpan = Math.sqrt(Math.max(0, exclR * exclR - dy * dy));
            colX = rowDir > 0
              ? craneX + exclSpan + CAST_JOINT_GAP
              : craneX - exclSpan - CAST_JOINT_GAP;
            continue;
          }

          // Slab polygon check
          if (!panelInsideSlab(cx, cy, pw / 2, ph / 2)) {
            colX = rowDir > 0 ? cx + pw / 2 + CAST_JOINT_GAP : cx - pw / 2 - CAST_JOINT_GAP;
            continue;
          }

          // Clash with placed panel — skip past it
          const clash = clashBox(cx, cy, pw, ph);
          if (clash) {
            colX = rowDir > 0
              ? clash.maxX + CAST_JOINT_GAP
              : clash.minX - CAST_JOINT_GAP;
            continue;
          }

          // Fits — place it (rotated 90° only for landscape panels).
          const mat = calcMaterials(panel);
          placed.push({
            panel, cx, cy,
            w: panel.mpvar.width,
            h: panel.mpvar.height,
            rotationRad: overflowRot,
            weightTons:   mat.panelWeightTons,
            pickOrder,
            pickRadiusIn: Math.hypot(cx - craneX, cy - craneY),
            isStacked:    false,
            stackLevel:   0,
            isNearWall:   false,
          });
          // AABB of the rotated panel: X-extent = h/2, Y-extent = w/2
          boxes.push({ minX: cx - pw / 2, maxX: cx + pw / 2, minY: cy - ph / 2, maxY: cy + ph / 2 });
          rowH = Math.max(rowH, ph);
          colX = rowDir > 0 ? cx + pw / 2 + CAST_JOINT_GAP : cx - pw / 2 - CAST_JOINT_GAP;
          done = true;
        }
      }
    }
  }

  // ── 4. Sort by pick order ────────────────────────────────────────────────────
  placed.sort((a, b) => a.pickOrder - b.pickOrder);

  return {
    placed, craneX, craneY,
    craneExclusionActual: CRANE_BODY_RADIUS_IN + settings.craneExclusionIn,
    usableMinX, usableMinY, usableMaxX, usableMaxY,
    perimOrder,
  };
}
